import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { LobbyMember, LobbyTeam } from "../../services/generated/client.js";
import { cn } from "../../lib/utils.js";

/**
 * Teams, and who is on them.
 *
 * Listed in `.claude/rules/frontend.md`'s board vocabulary since phase 0 and
 * built in phase 10 — because until then **Team mode could not be played at
 * all.** `roster_to_sides` refuses a player who is on no team, correctly, and
 * nothing in the frontend could put anybody on one.
 *
 * # Everybody moves themselves
 *
 * `party.join_team` lets anyone who can act move *themselves*; only the host
 * creates teams. That is the server's rule and this mirrors it, which is why
 * the buttons say "join" rather than "assign" — a host dragging people around a
 * roster is a different feature with a different failure mode.
 *
 * # Nobody unassigned, or the game will not start
 *
 * The engine's refusal names the person, and a host reading "Ben is not on a
 * team yet" acts on it. So this shows who is still unassigned rather than
 * leaving a host to compare two lists.
 */
export function TeamPanel({
  teams,
  members,
  you,
  isHost,
  onCreate,
  onJoin,
  busy = false,
}: {
  teams: readonly LobbyTeam[];
  /** Everybody in the party. Displays are filtered out here, not by the caller. */
  members: readonly LobbyMember[];
  /** The viewer's profile id, so their own row can say "you". */
  you: string | null;
  /** Whether to offer team creation. */
  isHost: boolean;
  onCreate: (name: string) => void;
  /** Join a team, or `null` to leave one. */
  onJoin: (teamId: string | null) => void;
  busy?: boolean;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState("");

  // A display holds a party code and no timeline (ADR 0005), so it is not on a
  // team and must not appear as somebody who is missing one.
  const players = members.filter((member) => member.role !== "display");
  const unassigned = players.filter((member) => !member.team_id);

  return (
    <section aria-label={t("teams.label")} className="flex flex-col gap-3">
      <h2 className="text-sm font-semibold">{t("teams.label")}</h2>

      {teams.length === 0 && <p className="text-xs text-white/50">{t("teams.none")}</p>}

      <ul className="flex flex-col gap-2">
        {teams.map((team) => {
          const roster = players.filter((member) => member.team_id === team.id);
          const mine = roster.some((member) => member.profile_id === you);
          return (
            <li
              key={team.id}
              className={cn(
                "rounded-lg border px-3 py-2",
                mine ? "border-flash-500 bg-flash-500/10" : "border-white/15",
              )}
            >
              <div className="flex items-baseline justify-between gap-3">
                <span className="min-w-0 truncate font-medium">{team.name}</span>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => {
                    onJoin(mine ? null : team.id);
                  }}
                  className="shrink-0 rounded-lg border border-white/20 px-3 py-1 text-xs font-medium disabled:opacity-40"
                >
                  {mine ? t("teams.leave") : t("teams.join")}
                </button>
              </div>
              <p className="mt-1 truncate text-xs text-white/55">
                {roster.length === 0
                  ? t("teams.empty")
                  : roster
                      .map((member) =>
                        member.profile_id === you
                          ? t("teams.youAre", { name: member.display_name })
                          : member.display_name,
                      )
                      .join(", ")}
              </p>
            </li>
          );
        })}
      </ul>

      {unassigned.length > 0 && (
        // Named, because the engine's refusal names them: "Ben is not on a team
        // yet" is something a host can act on, and a disabled start button with
        // no reason is how they conclude the game is broken instead.
        <p className="text-xs text-coin-400">
          {t("teams.unassigned", {
            names: unassigned.map((m) => m.display_name).join(", "),
          })}
        </p>
      )}

      {isHost && (
        <form
          className="flex gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            const trimmed = name.trim();
            if (trimmed) {
              onCreate(trimmed);
              setName("");
            }
          }}
        >
          <input
            aria-label={t("teams.newLabel")}
            value={name}
            onChange={(event) => {
              setName(event.target.value);
            }}
            placeholder={t("teams.newPlaceholder")}
            className="min-h-11 min-w-0 flex-1 rounded-lg border border-white/15 bg-stage-900 px-3 text-sm"
          />
          <button
            type="submit"
            disabled={busy || name.trim() === ""}
            className="min-h-11 shrink-0 rounded-lg border border-white/20 px-3 text-sm font-medium disabled:opacity-40"
          >
            {t("teams.add")}
          </button>
        </form>
      )}
    </section>
  );
}
